import { computed, ref } from '@vue/composition-api';
import { CancelTokenSource } from 'axios';

import { Action, Aligned } from '@/types';
import { isAlignedSourceAware } from '@/guards';

import type {
  RecordId,
  CompareOverviewRecord,
  DependencyRecord,
} from '@/services/SalesforceService';

import useReactiveMap from './useReactiveMap';
import useDI from './useDI';
import useSelected from './useSelected';
import useToast from './useToast';
import { getSelectedFieldsBySObject } from './useCustomValueFields';

let ancestors = new Map<RecordId, Set<RecordId>>();
const indexed = useReactiveMap<RecordId, DependencyRecord>();
const children = useReactiveMap<RecordId, Set<RecordId>>();
const resolving = useReactiveMap<RecordId, CancelTokenSource>();
const selecting = useReactiveMap<string, true>();
const refreshing = useReactiveMap<string, true>();

export default function useRequiredReferences() {
  const { container, Tokens } = useDI();
  const selection = useSelected();
  const { toast } = useToast();

  const service = container.get(Tokens.SalesforceService);

  function addDependencyRecord(record: DependencyRecord, ancestor: RecordId) {
    const c = children.value.get(ancestor) ?? new Set();
    c.add(record.Id);
    children.value.set(ancestor, c);
    indexed.value.set(record.Id, record);
    const a = ancestors.get(record.Id) ?? new Set();
    a.add(ancestor);
    ancestors.set(record.Id, a);
  }

  function removeRequiredReferencesByAncestor(ancestor: RecordId) {
    const cts = resolving.value.get(ancestor);
    if (cts !== undefined) {
      cts.cancel();
      resolving.value.delete(ancestor);
    }
    const recordsKeys = children.value.get(ancestor);
    if (recordsKeys === undefined) {
      return;
    }
    for (const recordKey of recordsKeys) {
      const ancestorKeys = ancestors.get(recordKey);
      if (ancestorKeys === undefined) {
        continue;
      }
      ancestorKeys.delete(ancestor);
      if (ancestorKeys.size === 0) {
        indexed.value.delete(recordKey);
        ancestors.delete(recordKey);
      }
    }
    children.value.delete(ancestor);
  }

  function remove(record: DependencyRecord) {
    indexed.value.delete(record.Id);
    for (const set of children.value.values()) {
      set.delete(record.Id);
    }
    ancestors.delete(record.Id);
  }

  function update(
    record: DependencyRecord,
    mutation: { Exists: boolean; IsDuplicate: boolean; IsRequired: boolean }
  ) {
    const current = indexed.value.get(record.Id);
    if (current === undefined) {
      return;
    }
    current.Exists = mutation.Exists;
    current.IsDuplicate = mutation.IsDuplicate;
    current.IsRequired = mutation.IsRequired;
    indexed.value.set(record.Id, current);
  }

  async function refresh(record: DependencyRecord) {
    try {
      refreshing.value.set(record.Key, true);
      const statusUpdate = await service.getRequiredDependencyStatusUpdate(
        record.SObject,
        record.Key,
        record.Name
      );
      if (statusUpdate.Exists === true && statusUpdate.IsDuplicate === false) {
        remove(record);
        return;
      }
      update(record, statusUpdate);
    } finally {
      selecting.value.delete(record.Key);
    }
  }

  function isRefreshing(record: DependencyRecord) {
    return refreshing.value.get(record.Id) ?? false;
  }

  function reset() {
    ancestors = new Map();
    indexed.value = new Map();
    children.value = new Map();
    resolving.value = new Map();
  }

  async function resolve(record: CompareOverviewRecord, ancestor: RecordId) {
    if (isAlignedSourceAware(record.Aligned) === false) {
      return;
    }
    const { promise, cts } = service.getRequiredDependencies(record.Aligned);
    resolving.value.set(ancestor, cts);
    try {
      const requiredDependencies = await promise;
      requiredDependencies.forEach((requiredDependencyRecord) => {
        addDependencyRecord(requiredDependencyRecord, ancestor);
      });
    } catch (e) {
      selection.remove(record.Id);
      throw e;
    } finally {
      resolving.value.delete(ancestor);
    }
  }

  async function resolveRequiredRecordsForSelected(
    aligned: Aligned,
    ancestor: RecordId
  ) {
    if (isAlignedSourceAware(aligned) === false) {
      return Promise.resolve([]);
    }
    const { promise, cts } = service.getRequiredDependencies(aligned);
    resolving.value.set(ancestor, cts);

    try {
      const result = await promise;
      return result;
    } finally {
      resolving.value.delete(ancestor);
    }
  }

  async function compare(record: DependencyRecord) {
    const customValueFields = getSelectedFieldsBySObject(record.SObject);
    return service.compareOne(record.SObject, record.Key, customValueFields);
  }

  async function select(r: DependencyRecord, isRecursive = false) {
    const record = r.Parent ?? r;

    selecting.value.set(record.Id, true);

    try {
      const customValueFields = getSelectedFieldsBySObject(record.SObject);
      const compareOverviewRecord = await service.compareOne(
        record.SObject,
        record.Key,
        customValueFields
      );
      if (compareOverviewRecord.Action === Action.DUPLICATE) {
        toast(
          `Duplicate requirement selected: ${record.SObject} with key ${record.Key}`,
          {
            dismissible: true,
          }
        );
        return;
      }
      selection.add(compareOverviewRecord);
      let requiredRecordsForSelected = await resolveRequiredRecordsForSelected(
        compareOverviewRecord.Aligned,
        compareOverviewRecord.Id
      );

      if (isRecursive) {
        requiredRecordsForSelected = requiredRecordsForSelected.filter(
          ({ Id }) =>
            selection.isNotInSelected(Id) && selecting.value.get(Id) === true
        );

        await Promise.all(
          requiredRecordsForSelected.map((reference) =>
            select(reference, isRecursive)
          )
        );
      } else {
        requiredRecordsForSelected.map((reference) =>
          addDependencyRecord(reference, record.Id)
        );
      }
    } finally {
      selecting.value.delete(record.Id);
    }
  }

  const allDependencies = computed(() => [...indexed.value.values()]);

  const records = computed(() =>
    allDependencies.value.filter((item) => {
      if (item.Exists) {
        return false;
      }

      let id = item.Id;

      if (item.Parent !== undefined) {
        id = item.Parent.Id;
      }

      return (
        selection.ids.value.includes(id) === false &&
        selecting.value.get(id) === undefined
      );
    })
  );

  const related = computed(() => {
    return allDependencies.value.filter((item) => {
      if (item.Exists === false) {
        return false;
      }

      let id = item.Id;

      if (item.Parent !== undefined) {
        id = item.Parent.Id;
      }

      return (
        selection.ids.value.includes(id) === false &&
        selecting.value.get(id) === undefined
      );
    });
  });

  const isResolving = computed(() => resolving.value.size !== 0);

  const isSelecting = computed(() => selecting.value.size !== 0);

  const isSelectingAll = ref(false);

  const isBusy = computed(
    () => isResolving.value || isSelecting.value || isSelectingAll.value
  );

  async function selectAll() {
    isSelectingAll.value = true;
    try {
      await Promise.all(records.value.map((record) => select(record, true)));
    } finally {
      isSelectingAll.value = false;
    }
  }

  const hasUnselectedRecords = computed(() => records.value.length > 0);

  return {
    records,
    related,
    isResolving,
    isSelecting,
    isBusy,
    hasUnselectedRecords,
    isSelectingAll,
    removeRequiredReferencesByAncestor,
    resolve,
    select,
    selectAll,
    reset,
    compare,
    refresh,
    isRefreshing,
  };
}
